import dayjs from 'dayjs';
import {
  InternalApiFinalRisk,
  InternalApiFinalRiskEventEnum,
  InternalApiFinalRiskLevel,
  InternalApiFinalRiskRangeEntry,
} from '../api-internal';
import { RiskHistoryDateRange } from './riskHistoryFilters';

export const PointType = {
  ...InternalApiFinalRiskEventEnum,
  START_RISK: 'START_RISK',
  END_RISK: 'END_RISK',
} as const;
export type PointType = (typeof PointType)[keyof typeof PointType];

export interface OverridePeriod {
  start: number;
  end: number | null;
  level: InternalApiFinalRiskLevel;
}

interface ScorePoint {
  time: number;
  score: number;
  baseLevel: InternalApiFinalRiskLevel;
  effectiveLevel: InternalApiFinalRiskLevel;
  effectiveSegmentLevel: InternalApiFinalRiskLevel;
  type: PointType;
}

export interface LevelThreshold {
  level: InternalApiFinalRiskLevel;
  score: number;
}

export interface RiskChartModel {
  scorePoints: ScorePoint[];
  overrides: OverridePeriod[];
  thresholds: LevelThreshold[];
}

const buildThresholds = (ranges: InternalApiFinalRiskRangeEntry[]): LevelThreshold[] =>
  ranges
    .toSorted((a, b) => a.scoreFrom - b.scoreFrom)
    .map((r) => ({ level: r.level, score: r.scoreFrom }))
    .filter((r) => r.score);

export const buildOverridePeriods = (events: InternalApiFinalRisk[]): OverridePeriod[] => {
  const periods: OverridePeriod[] = [];
  for (const r of events) {
    if (r.event === InternalApiFinalRiskEventEnum.OVERRIDE_STARTED) {
      periods.push({ start: r.createdTime.valueOf(), end: null, level: r.level });
    } else if (r.event === InternalApiFinalRiskEventEnum.OVERRIDE_ENDED) {
      const open = [...periods].reverse().find((p) => p.end === null);
      if (open) {
        open.end = r.createdTime.valueOf();
      }
    }
  }
  return periods;
};

export const effectiveLevelAt = (
  time: number,
  computedLevel: InternalApiFinalRiskLevel,
  overrides: OverridePeriod[]
): InternalApiFinalRiskLevel => {
  const active = overrides.find((p) => p.start <= time && (p.end === null || p.end > time));
  return active ? active.level : computedLevel;
};

export const buildScorePoints = (events: InternalApiFinalRisk[], overrides: OverridePeriod[]): ScorePoint[] => {
  const firstEventIndex = events.findIndex((event) => event.score !== undefined);
  if (firstEventIndex === -1) return [];

  let baseLevel = events[firstEventIndex].level;
  let lastScore: number = events[firstEventIndex].score!;

  return events.slice(firstEventIndex).map((event) => {
    if (event.event === InternalApiFinalRiskEventEnum.SCORE_CHANGED) {
      baseLevel = event.level;
      lastScore = event.score !== undefined ? event.score : lastScore;
    }
    const time = event.createdTime.valueOf();
    return {
      time,
      score: lastScore,
      baseLevel,
      effectiveLevel: event.level,
      effectiveSegmentLevel: effectiveLevelAt(time, baseLevel, overrides),
      type: event.event as unknown as PointType,
    };
  });
};

export const riskHistoryWindowStart = (dateRange: RiskHistoryDateRange, now: number): number | null =>
  dateRange === 'ALL' ? null : dayjs(now).subtract(Number(dateRange), 'day').valueOf();

const windowScorePoints = (
  points: ScorePoint[],
  windowStart: number | null,
  windowEnd: number,
  overrides: OverridePeriod[]
): ScorePoint[] => {
  const within = windowStart ? points.filter((p) => p.time >= windowStart) : points;
  const lastBeforeWindow = windowStart ? points.filter((p) => p.time < windowStart).at(-1) : undefined;
  const lastInWindow = within.at(-1);

  if (lastInWindow) {
    const endLevel = effectiveLevelAt(windowEnd, lastInWindow.baseLevel, overrides);
    within.push({
      time: windowEnd,
      score: lastInWindow.score,
      baseLevel: lastInWindow.baseLevel,
      effectiveLevel: endLevel,
      effectiveSegmentLevel: endLevel,
      type: PointType.END_RISK,
    });
  }

  if (windowStart && lastBeforeWindow) {
    const startLevel = effectiveLevelAt(windowStart, lastBeforeWindow.baseLevel, overrides);
    return [
      {
        time: windowStart,
        score: lastBeforeWindow.score,
        baseLevel: lastBeforeWindow.baseLevel,
        effectiveLevel: startLevel,
        effectiveSegmentLevel: startLevel,
        type: PointType.START_RISK,
      },
      ...within,
    ];
  }
  return within;
};

export const buildRiskChartModel = (
  events: InternalApiFinalRisk[],
  ranges: InternalApiFinalRiskRangeEntry[],
  dateRange: RiskHistoryDateRange,
  now: number
): RiskChartModel => {
  const sorted = events.toSorted((a, b) => a.createdTime.valueOf() - b.createdTime.valueOf());
  const overrides = buildOverridePeriods(sorted);
  const scorePoints = windowScorePoints(
    buildScorePoints(sorted, overrides),
    riskHistoryWindowStart(dateRange, now),
    now,
    overrides
  );
  return {
    scorePoints,
    overrides,
    thresholds: buildThresholds(ranges),
  };
};
